import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { Download } from 'lucide-react';
import { withRouter } from '../../../lib/withRouter';
import { formatDate, getViewPatientData } from '../../../lib';
import '../clr-tailwind.css';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { KpiCard } from '../ui/kpi-card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import ReferralDetail from '../ReferralListTab/ReferralDetail';
import ManageReferral from '../ReferralListTab/ManageReferral';
import ClosureOverviewCard from './cards/ClosureOverviewCard';
import ClosureTimeDistributionCard from './cards/ClosureTimeDistributionCard';
import MostUtilizedServicesCard from './cards/MostUtilizedServicesCard';
import ApprovalRateByPayerCard from './cards/ApprovalRateByPayerCard';
import AvgApprovalTurnaroundCard from './cards/AvgApprovalTurnaroundCard';
import DenialReasonsCard from './cards/DenialReasonsCard';
import ClrLoadingState from '../ui/ClrLoadingState';
import ClrDateFilter from './ClrDateFilter';
import ClrExportDialog, { CLR_EXPORT_REPORT_CONFIG } from './ClrExportDialog';
import ClrTableFilterMenu from './ClrTableFilterMenu';
import '../Dashboard.less';

function EditActionIcon() {
  return (
    <svg className="clr-icon" width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M7.5 1.49725H2.25C1.85218 1.49725 1.47064 1.65529 1.18934 1.93659C0.908035 2.2179 0.75 2.59943 0.75 2.99725V13.4973C0.75 13.8951 0.908035 14.2766 1.18934 14.5579C1.47064 14.8392 1.85218 14.9973 2.25 14.9973H12.75C13.1478 14.9973 13.5294 14.8392 13.8107 14.5579C14.092 14.2766 14.25 13.8951 14.25 13.4973V8.24725M12.2812 1.21599C12.5796 0.917622 12.9843 0.75 13.4062 0.75C13.8282 0.75 14.2329 0.917622 14.5312 1.21599C14.8296 1.51436 14.9972 1.91903 14.9972 2.34099C14.9972 2.76295 14.8296 3.16762 14.5312 3.46599L7.77148 10.2265C7.59339 10.4044 7.37338 10.5347 7.13173 10.6052L4.97698 11.2352C4.91244 11.2541 4.84403 11.2552 4.77891 11.2385C4.71379 11.2218 4.65435 11.1879 4.60681 11.1404C4.55928 11.0929 4.5254 11.0334 4.50871 10.9683C4.49203 10.9032 4.49316 10.8348 4.51198 10.7702L5.14198 8.61549C5.21287 8.37403 5.34338 8.15428 5.52148 7.97649L12.2812 1.21599Z"
        stroke="#5F7470"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DownloadActionIcon() {
  return (
    <svg className="clr-icon" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M9 11.25V2.25M12.75 7.5L9 11.25L5.25 7.5M15.75 11.25V14.25C15.75 14.6478 15.592 15.0294 15.3107 15.3107C15.0294 15.592 14.6478 15.75 14.25 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V11.25"
        stroke="#1F6F5C"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
const STATUS_BADGE = {
  Accepted: 'success',
  Approved: 'success',
  Declined: 'danger',
  Denied: 'danger',
  Pending: 'warning',
  'Outreach Initiated': 'outreach',
  'Outreach initiated': 'outreach',
  'Referral Loop Closed': 'muted',
};

const DEFAULT_FILTERS = {
  dateFilter: 'all',
  startDate: null,
  endDate: null,
  search: '',
  sortDir: 'asc',
};

function buildAnalyticsQuery(filters, serviceProviderId) {
  const params = new URLSearchParams();
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  if (serviceProviderId) params.set('serviceProviderId', serviceProviderId);
  return params;
}

const EMPTY_TABLE_FILTERS = {
  payers: [],
  statuses: [],
  referralTypes: [],
  organizations: [],
};

function joinCsv(values) {
  if (!Array.isArray(values) || values.length === 0) return '';
  return values.map((value) => String(value).trim()).filter(Boolean).join(',');
}

function buildTableQuery({
  serviceProviderId,
  sortDir = 'asc',
  search,
  startDate,
  endDate,
  tableFilters = EMPTY_TABLE_FILTERS,
  page = 1,
  pageSize = 50,
} = {}) {
  const params = new URLSearchParams();
  if (serviceProviderId) params.set('serviceProviderId', serviceProviderId);
  if (sortDir) params.set('sortDir', sortDir);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));

  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);

  const cin = search?.trim();
  if (cin) params.set('search', cin);

  const payers = joinCsv(tableFilters.payers);
  const statusIds = joinCsv(tableFilters.statuses);
  const referralTypeIds = joinCsv(tableFilters.referralTypes);
  const organizationIds = joinCsv(tableFilters.organizations);

  if (payers) params.set('payers', payers);
  if (statusIds) params.set('statusIds', statusIds);
  if (referralTypeIds) params.set('referralTypeIds', referralTypeIds);
  if (organizationIds) params.set('organizationIds', organizationIds);

  return params;
}

const CLR_PAGE_SIZE_OPTIONS = [50, 75, 100];
const DEFAULT_CLR_PAGE_SIZE = 50;

/** Compact 1-based page list with ellipsis gaps (`null`). */
function getVisiblePageNumbers(currentPage, totalPages) {
  const total = Math.max(0, Number(totalPages) || 0);
  const current = Math.max(1, Math.min(total || 1, Number(currentPage) || 1));
  if (total <= 0) return [];
  if (total <= 4) return Array.from({ length: total }, (_, index) => index + 1);

  const pages = new Set([1, total]);
  if (current <= 2) {
    pages.add(2);
  } else if (current >= total - 1) {
    pages.add(total - 1);
  } else {
    pages.add(current - 1);
    pages.add(current);
    pages.add(current + 1);
  }

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result = [];
  sorted.forEach((pageNumber, index) => {
    if (index > 0 && pageNumber - sorted[index - 1] > 1) result.push(null);
    result.push(pageNumber);
  });
  return result;
}

function PageSizeSelect({
  id, value, options, disabled, onChange,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className={`clr-page-size${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        id={id}
        className="clr-page-size__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Results per page"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{value}</span>
        <svg
          className="clr-icon clr-page-size__chevron"
          width="18"
          height="18"
          viewBox="0 0 12 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M1 1.5L6 6.5L11 1.5"
            stroke="#5F7470"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open ? (
        <div className="clr-page-size__menu" role="listbox" aria-label="Results per page">
          {options.map((size) => (
            <button
              key={size}
              type="button"
              role="option"
              aria-selected={size === value}
              className={`clr-page-size__option${size === value ? ' is-selected' : ''}`}
              onClick={() => {
                onChange(size);
                setOpen(false);
              }}
            >
              {size}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

PageSizeSelect.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  options: PropTypes.arrayOf(PropTypes.number).isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

function PagerChevron({ direction }) {
  const points = direction === 'left' ? 'M5.5 1L1.5 5L5.5 9' : 'M1.5 1L5.5 5L1.5 9';
  return (
    <svg className="clr-icon" width="18" height="18" viewBox="0 0 7 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d={points} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function analyticsDateFilterToDates(dateFilter, customStart, customEnd) {
  const end = moment().format('YYYY-MM-DD');
  switch (dateFilter) {
    case 'today':
      return { startDate: moment().startOf('day').format('YYYY-MM-DD'), endDate: end };
    case 'last_7_days':
      return { startDate: moment().subtract(6, 'days').startOf('day').format('YYYY-MM-DD'), endDate: end };
    case 'last_30_days':
      return { startDate: moment().subtract(29, 'days').startOf('day').format('YYYY-MM-DD'), endDate: end };
    case 'this_month':
      return {
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
      };
    case 'last_month':
      return {
        startDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        endDate: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      };
    case 'ytd':
      return { startDate: moment().startOf('year').format('YYYY-MM-DD'), endDate: end };
    case 'all':
      return { startDate: null, endDate: null };
    case 'custom':
      return { startDate: customStart, endDate: customEnd };
    default:
      return { startDate: moment().subtract(29, 'days').format('YYYY-MM-DD'), endDate: end };
  }
}

function ReferralPerformanceDashboard({ settings }) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [tableDateFilter, setTableDateFilter] = useState('all');
  const [tableCustomStartDate, setTableCustomStartDate] = useState(null);
  const [tableCustomEndDate, setTableCustomEndDate] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [referralList, setReferralList] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState(null);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [sidebarView, setSidebarView] = useState(null);
  const [referralStatusDropdown, setReferralStatusDropdown] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    payers: [],
    statuses: [],
    referralTypes: [],
    organizations: [],
  });
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [tableFilters, setTableFilters] = useState(EMPTY_TABLE_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_CLR_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const rootRef = useRef(null);
  const referralTrackingRef = useRef(null);
  const tableToolbarRef = useRef(null);

  const serviceProviderId = localStorage.getItem('serviceProviderId');
  const baseUrl = settings?.server?.baseUrl;

  const resolvedFilters = useMemo(() => {
    const dates = analyticsDateFilterToDates(
      filters.dateFilter,
      filters.startDate,
      filters.endDate,
    );
    return {
      ...filters,
      startDate: dates.startDate,
      endDate: dates.endDate,
    };
  }, [filters]);

  const mapFilterOptions = (items = []) => (
    items
      .filter((item) => item?.name != null && item?.id != null && String(item.name).trim() !== '')
      .map((item) => ({
        value: String(item.id),
        label: String(item.name),
      }))
  );

  const fetchFilterOptions = useCallback(async () => {
    try {
      const data = await getViewPatientData({ url: `${baseUrl}/referral/filters`, server: settings.server });
      const statuses = mapFilterOptions(data?.statuses);
      setReferralStatusDropdown(data?.statuses || []);
      setFilterOptions({
        payers: mapFilterOptions(data?.payers),
        statuses,
        referralTypes: mapFilterOptions(data?.referralTypes),
        organizations: mapFilterOptions(data?.organizations),
      });
    } catch (e) {
      console.error(e);
    }
  }, [baseUrl, settings.server]);

  const fetchAnalytics = useCallback(async () => {
    if (resolvedFilters.dateFilter === 'custom'
      && (!resolvedFilters.startDate || !resolvedFilters.endDate)) {
      return;
    }
    try {
      setLoadingAnalytics(true);
      setError(null);
      const params = buildAnalyticsQuery(resolvedFilters, serviceProviderId);
      const data = await getViewPatientData({
        url: `${baseUrl}/referral/analytics?${params.toString()}`,
        server: settings.server,
      });
      setAnalytics(data);
    } catch (e) {
      setError(e.message || 'Failed to load analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  }, [baseUrl, resolvedFilters, serviceProviderId, settings.server]);

  const resolvedTableDates = useMemo(
    () => analyticsDateFilterToDates(
      tableDateFilter,
      tableCustomStartDate,
      tableCustomEndDate,
    ),
    [tableDateFilter, tableCustomStartDate, tableCustomEndDate],
  );

  const fetchClosureDetails = useCallback(async () => {
    if (tableDateFilter === 'custom'
      && (!resolvedTableDates.startDate || !resolvedTableDates.endDate)) {
      return;
    }
    try {
      setLoadingList(true);
      setError(null);

      const params = buildTableQuery({
        serviceProviderId,
        sortDir: filters.sortDir,
        search: appliedSearch,
        startDate: resolvedTableDates.startDate,
        endDate: resolvedTableDates.endDate,
        tableFilters,
        page,
        pageSize,
      });
      const data = await getViewPatientData({
        url: `${baseUrl}/referral/closure-details?${params.toString()}`,
        server: settings.server,
      });
      const rows = data?.referralData || [];
      const nextTotal = Number(data?.totalCount) || 0;
      const nextPageSize = Number(data?.pageSize) || pageSize;
      const nextTotalPages = Number(data?.totalPages) >= 0
        ? Number(data.totalPages)
        : (nextPageSize > 0 ? Math.ceil(nextTotal / nextPageSize) : 0);

      setReferralList(rows);
      setTotalCount(nextTotal);
      setTotalPages(nextTotalPages);

      if (nextTotalPages > 0 && page > nextTotalPages) {
        setPage(nextTotalPages);
      }
    } catch (e) {
      setError(e.message || 'Failed to load referral details');
    } finally {
      setLoadingList(false);
    }
  }, [
    appliedSearch,
    baseUrl,
    filters.sortDir,
    page,
    pageSize,
    resolvedTableDates.endDate,
    resolvedTableDates.startDate,
    serviceProviderId,
    settings.server,
    tableDateFilter,
    tableFilters,
  ]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Keep CSS vars in sync with real sticky/toolbar heights (min-heights + layout).
  useEffect(() => {
    const root = rootRef.current;
    const tracking = referralTrackingRef.current;
    if (!root || !tracking) return undefined;

    const syncStickyOffsets = () => {
      const trackingHeight = Math.ceil(tracking.getBoundingClientRect().height);
      root.style.setProperty('--clr-sticky-header-height', `${trackingHeight}px`);
      const toolbar = tableToolbarRef.current;
      if (toolbar) {
        const toolbarHeight = Math.ceil(toolbar.getBoundingClientRect().height);
        root.style.setProperty('--clr-table-toolbar-height', `${toolbarHeight}px`);
      }
    };

    syncStickyOffsets();
    const observer = new ResizeObserver(syncStickyOffsets);
    observer.observe(tracking);
    const toolbar = tableToolbarRef.current;
    if (toolbar) observer.observe(toolbar);
    window.addEventListener('resize', syncStickyOffsets);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncStickyOffsets);
    };
  }, [loadingAnalytics, loadingList, analytics]);

  useEffect(() => {
    fetchClosureDetails();
  }, [fetchClosureDetails]);

  useEffect(() => {
    if (resolvedFilters.dateFilter === 'custom'
      && (!resolvedFilters.startDate || !resolvedFilters.endDate)) {
      return;
    }
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleDateFilterChange = (value) => {
    if (value === 'custom') {
      setFilters((f) => ({
        ...f,
        dateFilter: value,
        startDate: null,
        endDate: null,
      }));
      return;
    }
    const dates = analyticsDateFilterToDates(value);
    setFilters((f) => ({
      ...f,
      dateFilter: value,
      startDate: dates.startDate,
      endDate: dates.endDate,
    }));
  };

  const handleDateRangeChange = (start, end) => {
    setFilters((f) => ({
      ...f,
      dateFilter: 'custom',
      startDate: moment(start).startOf('day').format('YYYY-MM-DD'),
      endDate: moment(end).endOf('day').format('YYYY-MM-DD'),
    }));
  };

  const handleAnalyticsCustomRangeReset = () => {
    setFilters((f) => ({
      ...f,
      dateFilter: 'custom',
      startDate: null,
      endDate: null,
    }));
  };

  const handleApplySearch = () => {
    setPage(1);
    setAppliedSearch(searchInput.trim());
  };

  const handleSearchInputChange = (event) => {
    const nextValue = event.target.value;
    setSearchInput(nextValue);
    if (!nextValue.trim() && appliedSearch) {
      setPage(1);
      setAppliedSearch('');
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setPage(1);
    setAppliedSearch('');
  };

  const handleApplyTableFilters = (nextFilters) => {
    setPage(1);
    setTableFilters({
      payers: nextFilters?.payers || [],
      statuses: nextFilters?.statuses || [],
      referralTypes: nextFilters?.referralTypes || [],
      organizations: nextFilters?.organizations || [],
    });
  };

  const handleTableDateFilterChange = (value) => {
    setPage(1);
    setTableDateFilter(value);
    if (value !== 'custom') {
      setTableCustomStartDate(null);
      setTableCustomEndDate(null);
    }
  };

  const handleTableDateRangeChange = (start, end) => {
    setPage(1);
    setTableDateFilter('custom');
    setTableCustomStartDate(moment(start).startOf('day').format('YYYY-MM-DD'));
    setTableCustomEndDate(moment(end).endOf('day').format('YYYY-MM-DD'));
  };

  const handleTableCustomRangeReset = () => {
    setPage(1);
    setTableDateFilter('custom');
    setTableCustomStartDate(null);
    setTableCustomEndDate(null);
  };

  const handlePageChange = (nextPage) => {
    const bounded = Math.max(1, Math.min(nextPage, Math.max(1, totalPages || 1)));
    if (bounded === page || loadingList) return;
    setPage(bounded);
  };

  const handlePageSizeChange = (nextSize) => {
    const size = Number(nextSize);
    if (!CLR_PAGE_SIZE_OPTIONS.includes(size) || size === pageSize) return;
    setPage(1);
    setPageSize(size);
  };

  // Same as ReferralListTab: exportMultiFiles with selected report + month/quarter.
  // OTF and MIF buttons share this path (format arg unused by the API call).
  const handleExport = async ({ reportType, exportDate, isMonthly } = {}) => {
    try {
      const selectedReport = reportType || 'ecm_rt';
      const periodDate = exportDate instanceof Date ? exportDate : new Date();
      const monthly = isMonthly
        ?? (CLR_EXPORT_REPORT_CONFIG[selectedReport]?.freq === 'monthly');

      const year = periodDate.getFullYear();
      const month = periodDate.getMonth() + 1;
      const quarter = Math.ceil(month / 3);

      let exportUrl = `${baseUrl}/referral/exportMultiFiles?`;
      exportUrl += monthly
        ? `month=${month}&year=${year}`
        : `quarter=${quarter}&year=${year}`;
      exportUrl += `&reportType=${selectedReport}`;
      if (serviceProviderId) exportUrl += `&serviceProviderId=${serviceProviderId}`;

      const blob = await getViewPatientData({
        url: exportUrl,
        jsonBody: false,
        responseType: 'blob',
        Accept: 'text/tab-separated-values',
        server: settings.server,
      });

      const periodSuffix = monthly
        ? `${year}M${month}`
        : `${year}Q${quarter}`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `HPSJ_${selectedReport.toUpperCase()}_${periodSuffix}.txt`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setExportDialogOpen(false);
    }
  };

  const kpis = analytics?.kpis || {};
  const monthlyOverview = analytics?.monthlyOverview || [];
  const closureDist = analytics?.closureTimeDistribution || [];
  const serviceUtil = analytics?.serviceUtilization || [];
  const payerApprovalRates = analytics?.payerApprovalRates || [];
  const payerTurnaroundTimes = analytics?.payerTurnaroundTimes || [];
  const denialRows = analytics?.denialReasons || [];

  const handleDownloadJson = async (referral) => {
    try {
      const data = await getViewPatientData({
        url: `${baseUrl}/referral/${referral?.referralId}/download`,
        server: settings.server,
      });

      const now = new Date();
      const timestamp =
        now.getFullYear()
        + String(now.getMonth() + 1).padStart(2, '0')
        + String(now.getDate()).padStart(2, '0')
        + String(now.getHours()).padStart(2, '0')
        + String(now.getMinutes()).padStart(2, '0')
        + String(now.getSeconds()).padStart(2, '0');

      const fileName = `${data?.closedLoopReferral?.member?.mediCalCIN || referral?.memberCin || 'referral'}_${timestamp}.json`;
      const jsonString = JSON.stringify(data?.closedLoopReferral, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download json failed:', error);
      setError(error.message || 'Failed to download referral JSON');
    }
  };

  const mapListRowForSidebar = (row) => ({
    referralId: row.referralId,
    memberId: row.memberId,
    clientName: row.memberName,
    payerName: row.payerName,
    serviceType: row.serviceType,
    referralType: row.referralType,
    referringOrg: row.referringOrg || row.organizationName,
    receivingOrg: row.receivingOrg || row.organizationName,
    receivingProvider: row.receivingProvider,
    receivingPhone: row.receivingPhone,
    currentStatus: row.referralStatus,
    authorizationStatus: row.authorizationStatus,
    authorizationRequestedDate: row.authorizationRequestedDate,
    authorizationDecisionDate: row.authorizationDecisionDate,
    slaDate: row.slaDate || row.authorizationEndDate || row.closureDate,
    lastUpdate: row.lastUpdate || row.closureDate || row.referralDate,
    loopStatus: row.loopStatus || (row.closureDate ? 'Closed' : 'Open'),
    loopClosureReason: row.loopClosureReason || row.outcome || null,
    loopClosedDate: row.loopClosedDate || row.closureDate,
    referralDate: row.referralDate,
  });

  const dateRangeLabel = useMemo(() => {
    if (resolvedFilters.startDate && resolvedFilters.endDate) {
      return `${moment(resolvedFilters.startDate).format('MMM YYYY')} – ${moment(resolvedFilters.endDate).format('MMM YYYY')}`;
    }
    return 'Select date range';
  }, [resolvedFilters.startDate, resolvedFilters.endDate]);

  const kpiCards = [
    {
      label: 'Total Referrals',
      value: kpis.totalReferrals,
      format: 'number',
      trendLabel: kpis.totalReferralsTrend,
      trendUp: kpis.totalReferralsTrendUp,
    },
    {
      label: 'Closed Referrals',
      value: kpis.closedReferrals,
      format: 'number',
      trendLabel: kpis.closedReferralsTrend,
      trendUp: kpis.closedReferralsTrendUp,
    },
    {
      label: 'Closure Rate',
      value: kpis.closureRate,
      format: 'percent',
      trendLabel: kpis.closureRateTrend,
      trendUp: kpis.closureRateTrendUp,
    },
    {
      label: 'Avg. Closure Time',
      value: kpis.avgDaysToClosure,
      format: 'days',
      trendLabel: kpis.avgDaysTrend,
      trendUp: kpis.avgDaysTrendUp != null ? !kpis.avgDaysTrendUp : undefined,
    },
  ];

  const isInitialLoad = loadingAnalytics && analytics == null;

  const closureDetailsToolbar = (
    <div className="clr-table-toolbar" ref={tableToolbarRef}>
      <h2 className="clr-section-title">Referral Closure Details</h2>
      <div className="clr-table-toolbar-actions">
        <div className="clr-toolbar-search-wrap">
          <Input
            className="clr-toolbar-search"
            placeholder="Search CIN"
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyDown={(e) => e.key === 'Enter' && handleApplySearch()}
            aria-label="Search by CIN"
          />
          {searchInput ? (
            <button
              type="button"
              className="clr-toolbar-search-clear"
              aria-label="Clear search"
              onClick={handleClearSearch}
            >
              <svg className="clr-icon" width="18" height="18" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5" stroke="#5F7470" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          ) : null}
        </div>
        <ClrDateFilter
          value={tableDateFilter}
          onChange={handleTableDateFilterChange}
          customStartDate={tableCustomStartDate}
          customEndDate={tableCustomEndDate}
          onCustomRangeApply={handleTableDateRangeChange}
          onCustomRangeReset={handleTableCustomRangeReset}
        />
        <ClrTableFilterMenu
          payers={filterOptions.payers}
          statuses={filterOptions.statuses}
          referralTypes={filterOptions.referralTypes}
          organizations={filterOptions.organizations}
          value={tableFilters}
          onApply={handleApplyTableFilters}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="clr-toolbar-btn"
          aria-label="Export"
          onClick={() => setExportDialogOpen(true)}
        >
          <Download className="clr-icon" size={18} /> Export
        </Button>
      </div>
    </div>
  );

  return (
    <div id="clr-dashboard-root" ref={rootRef} className="min-h-screen bg-white pb-16">
      <div className="clr-tracking-sticky-scope">
        <div className="clr-referral-tracking-sticky" ref={referralTrackingRef}>
          <div className="clr-referral-tracking-header">
            <h1 className="clr-overview-title">Referral tracking</h1>
            <div className="clr-referral-tracking-filters">
              <ClrDateFilter
                value={filters.dateFilter}
                onChange={handleDateFilterChange}
                title={dateRangeLabel}
                customStartDate={filters.startDate}
                customEndDate={filters.endDate}
                onCustomRangeApply={handleDateRangeChange}
                onCustomRangeReset={handleAnalyticsCustomRangeReset}
              />
            </div>
          </div>
        </div>
        <div className="mx-auto max-w-[1400px] space-y-6 px-5 pt-6 pb-6">
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}

          {isInitialLoad ? (
            <ClrLoadingState message="Loading dashboard. Please wait..." className="clr-dashboard-initial-loading" />
          ) : (
            <>
          {/* Overview — KPIs */}
          <section className={`space-y-4${loadingAnalytics ? ' clr-analytics-loading' : ''}`}>
            <div className="clr-kpi-grid">
              {kpiCards.map((kpi) => (
                <KpiCard
                  key={kpi.label}
                  label={kpi.label}
                  value={kpi.value}
                  format={kpi.format}
                  trendLabel={kpi.trendLabel}
                  trendUp={kpi.trendUp}
                />
              ))}
            </div>
          </section>

          {/* Referral Closure */}
          <section className={`space-y-4${loadingAnalytics ? ' clr-analytics-loading' : ''}`}>
            <div>
              <h2 className="clr-section-title">Referral Closure</h2>
            </div>
            <div className="clr-closure-grid">
              <ClosureOverviewCard data={monthlyOverview} />
              <ClosureTimeDistributionCard data={closureDist} />
              <MostUtilizedServicesCard data={serviceUtil} />
            </div>
          </section>

          {/* Payer Performance */}
          <section className={`space-y-4${loadingAnalytics ? ' clr-analytics-loading' : ''}`}>
            <div>
              <h2 className="clr-section-title">Payer Performance</h2>
            </div>
            <div className="clr-payer-grid">
              <ApprovalRateByPayerCard data={payerApprovalRates} />
              <AvgApprovalTurnaroundCard data={payerTurnaroundTimes} />
              <DenialReasonsCard data={denialRows} />
            </div>
          </section>
            </>
          )}
        </div>
      </div>

      {!isInitialLoad && (
        <div className="mx-auto max-w-[1400px] space-y-6 px-5 pb-3">
          {/* Referral Closure Details */}
          <section className="space-y-4">
            <div className="clr-table-stack">
            <div className="clr-chart-card clr-table-card">
              {loadingList ? (
                <div className="clr-table-frame">
                  <div className="clr-table-sticky-pin">
                    {closureDetailsToolbar}
                  </div>
                  <div
                    className="clr-table-body-scroll clr-table-body-loading"
                    aria-busy="true"
                    aria-live="polite"
                  >
                    <ClrLoadingState message="Loading referrals. Please wait..." />
                  </div>
                </div>
              ) : (
              <Table
                className="clr-table--sticky-cols"
                toolbar={closureDetailsToolbar}
              >
                <TableHeader className="clr-table-head">
                  <TableRow>
                    <TableHead className="clr-table-actions-head">Actions</TableHead>
                    <TableHead>Member CIN</TableHead>
                    <TableHead>Member Name</TableHead>
                    <TableHead>Payer</TableHead>
                    <TableHead>Service Type</TableHead>
                    <TableHead>Referral Type</TableHead>
                    <TableHead>Organization Name</TableHead>
                    <TableHead>Referral Date</TableHead>
                    <TableHead>Referral Status</TableHead>
                    <TableHead>Outcome</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referralList.length === 0 && !loadingList && (
                    <TableRow>
                      <TableCell colSpan={10} className="clr-table-empty">No referrals found</TableCell>
                    </TableRow>
                  )}
                  {referralList.map((row) => {
                    const isEditDisabled = row.referralStatus === 'Referral Loop Closed';
                    return (
                    <TableRow
                      key={row.referralId}
                      className="cursor-pointer"
                      onClick={() => {
                        setSelectedReferral(mapListRowForSidebar(row));
                        setSidebarView('detail');
                      }}
                    >
                      <TableCell className="clr-table-actions-cell">
                        <div className="clr-table-actions">
                          <button
                            type="button"
                            className="clr-table-action-btn"
                            aria-label={
                              isEditDisabled
                                ? 'Edit referral status (unavailable for closed referrals)'
                                : 'Edit referral status'
                            }
                            title={
                              isEditDisabled
                                ? 'Cannot edit closed referrals'
                                : 'Edit referral status'
                            }
                            disabled={isEditDisabled}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (isEditDisabled) return;
                              setSelectedReferral(mapListRowForSidebar(row));
                              setSidebarView('update');
                            }}
                          >
                            <EditActionIcon />
                          </button>
                          <button
                            type="button"
                            className="clr-table-action-btn"
                            aria-label="Download referral JSON"
                            title="Download JSON"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDownloadJson(row);
                            }}
                          >
                            <DownloadActionIcon />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>{row.memberCin}</TableCell>
                      <TableCell className="clr-table-emphasis">{row.memberName}</TableCell>
                      <TableCell>{row.payerName}</TableCell>
                      <TableCell>{row.serviceType}</TableCell>
                      <TableCell>{row.referralType}</TableCell>
                      <TableCell>{row.organizationName}</TableCell>
                      <TableCell>{formatDate(row.referralDate)}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_BADGE[row.referralStatus] || 'outline'}>
                          {row.referralStatus}
                        </Badge>
                      </TableCell>
                      <TableCell>{row.outcome}</TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              )}
            </div>
              <div
                className="clr-table-pagination"
                role="navigation"
                aria-label={`Referral list pages, ${totalCount} referrals`}
              >
                <div className="clr-table-pagination__pages">
                  <button
                    type="button"
                    className="clr-table-pagination__nav"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={loadingList || page <= 1}
                    aria-label="Previous page"
                  >
                    <PagerChevron direction="left" />
                    <span>Back</span>
                  </button>
                  <div className="clr-table-pagination__numbers">
                    {getVisiblePageNumbers(page, Math.max(totalPages, 1)).map((pageNumber, index) => (
                      pageNumber == null ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="clr-table-pagination__ellipsis"
                          aria-hidden="true"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={pageNumber}
                          type="button"
                          className={`clr-table-pagination__page${pageNumber === page ? ' is-active' : ''}`}
                          onClick={() => handlePageChange(pageNumber)}
                          disabled={loadingList || totalPages === 0}
                          aria-current={pageNumber === page ? 'page' : undefined}
                          aria-label={`Go to page ${pageNumber}`}
                        >
                          {pageNumber}
                        </button>
                      )
                    ))}
                  </div>
                  <button
                    type="button"
                    className="clr-table-pagination__nav"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={loadingList || page >= totalPages || totalPages === 0}
                    aria-label="Next page"
                  >
                    <span>Next</span>
                    <PagerChevron direction="right" />
                  </button>
                </div>
                <div className="clr-table-pagination__page-size">
                  <label className="clr-table-pagination__page-size-label" htmlFor="clr-page-size">
                    Result per page
                  </label>
                  <PageSizeSelect
                    id="clr-page-size"
                    value={pageSize}
                    options={CLR_PAGE_SIZE_OPTIONS}
                    disabled={loadingList}
                    onChange={handlePageSizeChange}
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      <ClrExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onExportOtf={handleExport}
        onExportMif={handleExport}
      />

      {sidebarView === 'detail' && selectedReferral && (
        <ReferralDetail
          settings={settings}
          selectedReferral={selectedReferral}
          onClose={() => { setSelectedReferral(null); setSidebarView(null); }}
        />
      )}
      {sidebarView === 'update' && selectedReferral && (
        <ManageReferral
          settings={settings}
          selectedReferral={selectedReferral}
          referralStatusDropdown={referralStatusDropdown}
          onClose={() => { setSelectedReferral(null); setSidebarView(null); }}
          onSuccess={() => {
            setSelectedReferral(null);
            setSidebarView(null);
            fetchClosureDetails();
            fetchAnalytics();
          }}
        />
      )}
    </div>
  );
}

ReferralPerformanceDashboard.propTypes = {
  settings: PropTypes.object.isRequired,
};

export default connect((state) => state)(withRouter(ReferralPerformanceDashboard));
